// src/components/ReturnsArchiveViewerV2.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

import { buildReturnsV2PdfSpec, pdfDownloadFromSpec } from "../pdf/returnsV2PdfSpecs";
import { getThumbsForReturnsV2 } from "../pdf/spImages";

const RETURNS_COL = "returns_v2";
const PAGE_SIZE = 30;

/* ----------------- shared helpers (copied from your viewer, trimmed) ----------------- */

function getFormFromDoc(d) {
  if (!d) return {};
  if (d.type === "cyl") return d.cyl?.form || {};
  if (d.type === "parts") return d.parts?.form || {};
  return d.cyl?.form || d.parts?.form || {};
}

function statusLabel(s) {
  return String(s || "new");
}

function statusClasses(status) {
  switch (status) {
    case "new":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "waiting_collection":
    case "waiting_processing":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "in_progress":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "complete":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "straggler":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
}

function isDateInRange(ts, fromDate, toDate) {
  if (!ts?.toDate) return true;
  const dt = ts.toDate();
  if (fromDate) {
    const from = new Date(fromDate + "T00:00:00");
    if (dt < from) return false;
  }
  if (toDate) {
    const to = new Date(toDate + "T23:59:59");
    if (dt > to) return false;
  }
  return true;
}

/* ----------------- small UI bits ----------------- */

function Modal({ open, onClose, title, children, widthClass = "max-w-6xl" }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute inset-0 flex items-start justify-center p-4 overflow-auto">
        <div className={`w-full ${widthClass} bg-white rounded-xl shadow-2xl border border-gray-200`}>
          <div className="p-4 border-b flex items-center gap-3">
            <div className="text-lg font-semibold text-gray-900">{title}</div>
            <button
              type="button"
              className="ml-auto px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
              onClick={onClose}
            >
              Close
            </button>
          </div>
          <div className="p-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

function Chip({ text, onRemove }) {
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border text-sm bg-gray-50">
      {text}
      {onRemove ? (
        <button type="button" className="text-red-600" onClick={onRemove} title="Remove">
          ×
        </button>
      ) : null}
    </span>
  );
}

function KV({ label, value, mono }) {
  return (
    <div className="flex gap-2">
      <div className="w-52 text-gray-600">{label}</div>
      <div className={mono ? "font-mono" : ""}>{value || "—"}</div>
    </div>
  );
}

function bool(v) {
  return v ? "Yes" : "No";
}

function Section({ title, children }) {
  return (
    <div className="border rounded-xl p-4 bg-white">
      <div className="text-base font-semibold mb-3">{title}</div>
      {children}
    </div>
  );
}

/* ----------------- Read-only report view (same data view as your viewer) ----------------- */

function ReportViewReadOnly({ docData }) {
  const d = docData;
  const f = getFormFromDoc(d);

  if (d.type === "parts") {
    const t = d.parts?.templateSnapshot || {};
    const steps = t.steps || [];
    const test = d.parts?.test || {};

    return (
      <div className="space-y-4">
        <Section title="Return & Part">
          <div className="space-y-1 text-sm">
            <KV label="Returns Note #" value={f.returnsNoteNumber} />
            <KV label="Serial" value={f.serialNumber} mono />
            <KV label="SRET #" value={f.sretNumber} mono />
            <KV label="Part Code" value={f.partCode} />
            <KV label="Part Title" value={f.partTitle || t.title} />
            <KV label="Part Description" value={f.partDescription || t.description} />
          </div>
        </Section>

        <Section title="Reported / Fault Found">
          <div className="space-y-1 text-sm">
            <KV label="Reported" value={f.reportedCode} />
            <KV label="Fault Found" value={f.faultFoundCode} />
          </div>
        </Section>

        {steps.length > 0 ? (
          <Section title="Test Procedure">
            <div className="space-y-2 text-sm">
              {steps.map((st) => {
                const v = test[st.id];
                const shown = st.type === "check" ? (v ? "Yes" : "No") : String(v || "—");
                return (
                  <div key={st.id} className="flex gap-3 border rounded px-3 py-2">
                    <div className="w-72 text-gray-700">{st.label}</div>
                    <div className="flex-1">{shown}</div>
                    {st.required ? <span className="text-xs text-rose-600">Required</span> : null}
                  </div>
                );
              })}
            </div>
          </Section>
        ) : null}

        {f.internalNotes ? (
          <Section title="Warehouse / Engineer Notes">
            <div className="text-sm whitespace-pre-wrap">{f.internalNotes}</div>
          </Section>
        ) : null}

        {d.adminNotes ? (
          <Section title="Office Notes">
            <div className="text-sm whitespace-pre-wrap text-red-700">{d.adminNotes}</div>
          </Section>
        ) : null}
      </div>
    );
  }

  // cylinder
  const c = d.cyl?.cylCheck || {};
  const leaks = d.cyl?.leaks || {};
  const returnedParts = d.cyl?.returnedParts || [];
  const parts = d.cyl?.parts || {};

  return (
    <div className="space-y-4">
      <Section title="Return & Cylinder">
        <div className="space-y-1 text-sm">
          <KV label="Returns Note #" value={f.returnsNoteNumber} />
          <KV label="Serial" value={f.serialNumber} mono />
          <KV label="SRET #" value={f.sretNumber} mono />
          <KV label="Model" value={f.cylinderModel} />
          <KV label="Capacity" value={f.capacity} />
          <KV label="Manufacture Date" value={f.dateOfManufacture} />
        </div>
      </Section>

      <Section title="Reasons & Fault Found">
        <div className="space-y-1 text-sm">
          <KV label="Return Reason" value={f.reasonCode} />
          <KV label="Reason Defect" value={f.reasonDefectCode} />
          <KV label="Fault Found" value={f.faultFoundCode} />
          <KV label="Fault Defect" value={f.faultDefectCode} />
        </div>
      </Section>

      <Section title="Cylinder Checklist">
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          <KV label="Installed" value={bool(c.cylinderInstalled)} />
          <KV label="Damage: Case" value={bool(c.damageToCase)} />
          <KV label="Damage: Packaging" value={bool(c.damageToPackaging)} />
          <KV label="Damage: Bosses" value={bool(c.damageToBosses)} />
          <KV label="Damage: Pipework/Parts" value={bool(c.damageToPipeworkParts)} />
          <KV label="Damage: Foam" value={bool(c.damageToFoam)} />
          <KV label="Strip & Test" value={bool(c.stripAndTest)} />
        </div>
      </Section>

      <Section title="Leak Checklist">
        <div className="grid sm:grid-cols-2 gap-2 text-sm">
          {Object.keys(leaks || {})
            .filter((k) => k !== "otherComments")
            .map((k) => (
              <KV key={k} label={k} value={bool(leaks[k])} />
            ))}
          {leaks?.other ? <KV label="Other Comments" value={leaks.otherComments} /> : null}
        </div>
      </Section>

      <Section title="Parts Return">
        <div className="space-y-2 text-sm">
          <KV label="No parts returned" value={bool(parts.noPartsReturned)} />
          <KV label="All parts returned" value={bool(parts.allPartsReturned)} />
          <KV label="Partial return" value={bool(parts.partialReturn)} />
          <KV label="System type" value={parts.systemType} />
          <KV label="Returned parts list" value={(returnedParts || []).join(", ")} />
          {parts.notes ? <KV label="Parts notes" value={parts.notes} /> : null}
          {parts.rmaRef ? <KV label="RMA / Ref" value={parts.rmaRef} /> : null}
        </div>
      </Section>

      {f.internalNotes ? (
        <Section title="Warehouse / Engineer Notes">
          <div className="text-sm whitespace-pre-wrap">{f.internalNotes}</div>
        </Section>
      ) : null}

      {d.adminNotes ? (
        <Section title="Office Notes">
          <div className="text-sm whitespace-pre-wrap text-red-700">{d.adminNotes}</div>
        </Section>
      ) : null}
    </div>
  );
}

function ImagesPanelV2({ docId, storage, type }) {
  const [loading, setLoading] = useState(false);
  const [folderUrl, setFolderUrl] = useState(storage?.folderUrl || null);
  const [thumbs, setThumbs] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const folderId = storage?.folderId;
      setFolderUrl(storage?.folderUrl || null);

      if (!folderId) {
        setThumbs([]);
        return;
      }

      setLoading(true);
      try {
        const data = await getThumbsForReturnsV2({ returnsId: docId, storage, type, take: 6 });
        if (cancelled) return;
        setThumbs(data || []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setThumbs([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [docId, storage?.folderId, type, storage?.folderUrl]);

  if (!storage?.folderId && !thumbs.length) return null;

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold text-sm">Photos</div>
        {folderUrl ? (
          <a className="text-xs text-indigo-700 underline" href={folderUrl} target="_blank" rel="noreferrer">
            Open folder
          </a>
        ) : null}
      </div>

      {loading ? <div className="text-xs text-gray-500">Loading images…</div> : null}

      {!loading && thumbs.length === 0 ? (
        <div className="text-xs text-gray-500">No images available.</div>
      ) : null}

      <div className="flex flex-wrap gap-3">
        {thumbs.map((img) => (
          <a
            key={img.id}
            href={img.webUrl}
            target="_blank"
            rel="noreferrer"
            className="w-44 h-44 border rounded overflow-hidden bg-gray-100 block"
            title={img.name}
          >
            <img src={img.thumbUrl || img.webUrl} alt={img.name} className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}

/* ----------------- Detail modal (READ ONLY) ----------------- */

function DetailModalReadOnly({ open, onClose, row, onGeneratePdf }) {
  if (!row) return null;

  const created = row.createdAt?.toDate?.();
  const submitted = row.submittedAt?.toDate?.();
  const archivedAt = row.archivedAt?.toDate?.();
  const typeLabel = row.type === "cyl" ? "Cylinder" : row.type === "parts" ? "Parts" : row.type;

  return (
    <Modal open={open} onClose={onClose} title={`ARCHIVED — ${typeLabel} — ${row.id}`} widthClass="max-w-6xl">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`text-xs rounded px-2 py-1 border ${statusClasses(row.status)}`}>
            Status: {statusLabel(row.status)}
          </span>
          <span className="text-xs rounded px-2 py-1 border bg-gray-50">Type: {typeLabel}</span>
          <span className="text-xs rounded px-2 py-1 border bg-gray-50">
            Archive: <b>TRUE</b>
          </span>

          <span className="text-xs text-gray-500">Created: {created ? created.toLocaleString() : "—"}</span>
          <span className="text-xs text-gray-500">Submitted: {submitted ? submitted.toLocaleString() : "—"}</span>
          <span className="text-xs text-gray-500">ArchivedAt: {archivedAt ? archivedAt.toLocaleString() : "—"}</span>

          <div className="ml-auto flex gap-2">
            <button
              type="button"
              className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
              onClick={onGeneratePdf}
              title="Generate PDF"
            >
              PDF
            </button>
          </div>
        </div>

        <div className="text-xs text-gray-500">
          Read-only archive view. No edits, no unarchive, no writes.
        </div>

        <ReportViewReadOnly docData={row} />
        <ImagesPanelV2 docId={row.id} storage={row.storage} type={row.type} />
        <div className="h-2" />
      </div>
    </Modal>
  );
}

/* ===========================
   ✅ Archive Viewer (read-only)
   =========================== */

export default function ReturnsArchiveViewerV2() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // filters
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [type, setType] = useState("all"); // all | cyl | parts

  // pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [hasNext, setHasNext] = useState(true);
  const pageLastDocsRef = useRef([]);

  // detail modal
  const [openRowId, setOpenRowId] = useState(null);
  const openRow = useMemo(() => rows.find((r) => r.id === openRowId) || null, [rows, openRowId]);

  const buildServerQuery = ({ cursor }) => {
    const parts = [];

    // ✅ ONLY archived docs
    parts.push(where("archive", "==", true));

    // optional type filter
    if (type !== "all") parts.push(where("type", "==", type));

    // order newest archived first (fallback to createdAt if archivedAt missing)
    // NOTE: This requires archivedAt to exist consistently for these docs.
    // Your archiver/migrator sets archivedAt. If some old docs don't, they'll be missing here.
    parts.push(orderBy("archivedAt", "desc"));
    parts.push(orderBy("createdAt", "desc"));

    if (cursor) parts.push(startAfter(cursor));
    parts.push(limit(PAGE_SIZE));

    return query(collection(db, RETURNS_COL), ...parts);
  };

  const fetchPage = async (targetIndex) => {
    setLoading(true);
    setError("");

    try {
      let cursor = null;
      if (targetIndex > 0) cursor = pageLastDocsRef.current[targetIndex - 1] || null;

      const q = buildServerQuery({ cursor });
      const snap = await getDocs(q);

      const docs = snap.docs.map((d) => ({ id: d.id, _snap: d, ...d.data() }));
      setRows(docs);

      pageLastDocsRef.current[targetIndex] = snap.docs[snap.docs.length - 1] || null;
      setPageIndex(targetIndex);
      setHasNext(snap.size === PAGE_SIZE);
    } catch (e) {
      console.error(e);
      setError("Failed to load archived returns (Firestore index may be required).");
      setRows([]);
      setHasNext(false);
    } finally {
      setLoading(false);
    }
  };

  const resetAndFetchFirstPage = () => {
    pageLastDocsRef.current = [];
    setPageIndex(0);
    setHasNext(true);
    fetchPage(0);
  };

  useEffect(() => {
    resetAndFetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    resetAndFetchFirstPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      // local date range filter (createdAt based, like your main viewer)
      if (!isDateInRange(r.createdAt, fromDate, toDate)) return false;
      return true;
    });
  }, [rows, fromDate, toDate]);

  const generatePdf = async (row) => {
    try {
      const spec = await buildReturnsV2PdfSpec(row);
      await pdfDownloadFromSpec(spec);
    } catch (e) {
      console.error(e);
      alert(e?.message || "PDF generation failed.");
    }
  };

  const nextPage = () => {
    if (loading) return;
    if (!hasNext) return;
    if (!pageLastDocsRef.current[pageIndex]) return;
    fetchPage(pageIndex + 1);
  };

  const prevPage = () => {
    if (loading) return;
    if (pageIndex <= 0) return;
    fetchPage(pageIndex - 1);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="text-2xl font-bold">Returns Archive Viewer (V2)</div>

        <div className="sm:ml-auto flex flex-wrap gap-2">
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
            onClick={resetAndFetchFirstPage}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-4 gap-3 bg-white border border-gray-200 rounded-xl p-4">
        <div>
          <div className="text-xs text-gray-500 mb-1">From date (createdAt)</div>
          <input className="w-full border rounded px-3 py-2 text-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">To date (createdAt)</div>
          <input className="w-full border rounded px-3 py-2 text-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Type</div>
          <select className="w-full border rounded px-3 py-2 text-sm" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="all">All</option>
            <option value="cyl">Cylinders</option>
            <option value="parts">Parts</option>
          </select>
        </div>
        <div className="flex items-end">
          <button
            type="button"
            className="w-full px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
            onClick={() => {
              setFromDate("");
              setToDate("");
            }}
          >
            Clear dates
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-3 flex flex-wrap gap-2 items-center">
        <span className="text-sm text-gray-600">
          Showing <b>{filtered.length}</b> archived record(s) on this page (page size {PAGE_SIZE})
        </span>

        <Chip text="archive: true" />
        {type !== "all" ? <Chip text={`type: ${type}`} /> : null}
        {fromDate ? <Chip text={`from: ${fromDate}`} /> : null}
        {toDate ? <Chip text={`to: ${toDate}`} /> : null}
      </div>

      {error ? <div className="mt-3 text-sm text-red-600">{error}</div> : null}

      {/* List */}
      <div className="mt-4 space-y-3">
        {loading && rows.length === 0 ? <div className="text-gray-500">Loading…</div> : null}
        {!loading && filtered.length === 0 ? <div className="text-gray-500">No archived results (change filters).</div> : null}

        {filtered.map((r) => {
          const f = getFormFromDoc(r);
          const dt = r.createdAt?.toDate?.();
          const title =
            r.type === "cyl"
              ? f.cylinderModel || "—"
              : r.type === "parts"
              ? f.partCode || f.partTitle || "—"
              : "—";

          return (
            <div key={r.id} className="border rounded-xl bg-white">
              <div className="p-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs rounded px-2 py-1 border bg-gray-50">
                      {r.type === "cyl" ? "Cylinder" : r.type === "parts" ? "Parts" : r.type}
                    </span>

                    <span className="font-medium truncate max-w-xs sm:max-w-md">{title}</span>

                    <span className={`text-xs rounded px-2 py-1 border ${statusClasses(r.status)}`}>
                      {statusLabel(r.status)}
                    </span>

                    <span className="text-xs rounded px-2 py-1 border bg-gray-100 text-gray-700">
                      Archived
                    </span>
                  </div>

                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600">
                    <span>
                      RN: <span className="font-mono">{f.returnsNoteNumber || "—"}</span>
                    </span>
                    <span>
                      Serial: <span className="font-mono">{f.serialNumber || "—"}</span>
                    </span>
                    <span>
                      SRET: <span className="font-mono">{f.sretNumber || "—"}</span>
                    </span>
                    <span className="text-gray-400">ID: {r.id.slice(-8)}</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-end gap-2">
                  <div className="text-xs sm:text-sm text-gray-500">{dt ? dt.toLocaleString() : "—"}</div>

                  <button
                    type="button"
                    className="text-indigo-700 hover:underline text-xs sm:text-sm"
                    onClick={() => setOpenRowId(r.id)}
                  >
                    View
                  </button>

                  <button
                    type="button"
                    className="px-2 py-1 rounded-md border border-gray-300 hover:bg-gray-50 text-xs sm:text-sm"
                    onClick={() => generatePdf(r)}
                  >
                    PDF
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center gap-2">
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || pageIndex === 0}
          onClick={prevPage}
        >
          Prev
        </button>
        <span className="text-sm text-gray-600">Page {pageIndex + 1}</span>
        <button
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
          disabled={loading || !hasNext}
          onClick={nextPage}
        >
          Next
        </button>
      </div>

      <DetailModalReadOnly
        open={!!openRowId}
        onClose={() => setOpenRowId(null)}
        row={openRow}
        onGeneratePdf={() => openRow && generatePdf(openRow)}
      />
    </div>
  );
}
